/**
 * Hold-to-listen (Option A ear button) + player audio capability.
 *
 * The Alerts tab-row ear control registers a momentary hold here; LiveStreamPlayer
 * ORs that with its click-to-toggle listen state so either path unmutes the
 * camera mic. Players also publish whether the current stream has audio so the
 * ear can show the unavailable (crossed-out) state.
 */
import { create } from "zustand";

interface ListenInHoldState {
  /** Camera id the operator is holding-to-listen on, or null. */
  holdCameraId: string | null;
  setHold: (cameraId: string | null) => void;
  /** camera id → whether the active live player has audio for listen-in. */
  hasAudioByCamera: Record<string, boolean>;
  setHasAudio: (cameraId: string, hasAudio: boolean | null) => void;
}

export const useListenInHoldStore = create<ListenInHoldState>((set) => ({
  holdCameraId: null,
  setHold: (cameraId) =>
    set((state) =>
      state.holdCameraId === cameraId ? state : { holdCameraId: cameraId }
    ),
  hasAudioByCamera: {},
  setHasAudio: (cameraId, hasAudio) =>
    set((state) => {
      const key = String(cameraId);
      if (hasAudio == null) {
        if (!(key in state.hasAudioByCamera)) return state;
        const next = { ...state.hasAudioByCamera };
        delete next[key];
        return { hasAudioByCamera: next };
      }
      if (state.hasAudioByCamera[key] === hasAudio) return state;
      return {
        hasAudioByCamera: { ...state.hasAudioByCamera, [key]: hasAudio },
      };
    }),
}));

/** True while the Option A ear (or any hold source) is pressed for this camera. */
export const useIsHoldingListenIn = (
  cameraId: string | number | null | undefined
): boolean =>
  useListenInHoldStore((state) =>
    cameraId == null || cameraId === ""
      ? false
      : state.holdCameraId === String(cameraId)
  );

/** Known has-audio from an active player; undefined until a player reports. */
export const useListenInHasAudio = (
  cameraId: string | number | null | undefined
): boolean | undefined =>
  useListenInHoldStore((state) => {
    if (cameraId == null || cameraId === "") return undefined;
    const key = String(cameraId);
    return key in state.hasAudioByCamera
      ? state.hasAudioByCamera[key]
      : undefined;
  });
